var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Cookie } from '@mediaflowcom/mf-js-utils';
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import '../video-item/video-item';
import '../video-list/video-list';
import styles from './search.styles';
import { dictionary } from '@mf-cms/helpers';
import '@mf-cms/components/src/skeleton/skeleton';
import { getVideoSkeletonLayout } from '../skeleton/video-skeleton';
import { classMap } from 'lit/directives/class-map.js';
import { MFElement } from '../mfelement/mfelement';
let PSearch = class PSearch extends MFElement {
    constructor() {
        super(...arguments);
        this.videoBaseUrl = '';
        this.culture = 'en-GB';
        this.showRuntime = false;
        this.showDate = false;
        this.type = '';
        this.videoItemLayout = 'default';
        this.result = {
            pages: [],
            files: [],
            totalHits: 0
        };
    }
    static { this.styles = styles; }
    /**
     * Search for videos or pages
     * @returns {void}
     */
    async search() {
        if (!this.query) {
            return;
        }
        this.loading = true;
        // Fetch search results
        this.result = await this.fetchSearchResults();
        this.loading = false;
        // Dispatch search event for analytics
        window.dispatchEvent(new CustomEvent('action', {
            detail: {
                type: 'search',
                query: this.query,
                hits: this.result.totalHits,
                culture: this.culture
            }
        }));
    }
    /**
     * Fetch search results from API based on type
     * @returns {Promise<{ items: any[], totalHits: number }>} Search results
     */
    async fetchSearchResults() {
        const accessToken = Cookie.get('portal_access_token');
        if (this.type == 'video')
            return await this.fetchPlayPortalSearchResultsAsync(accessToken);
        return await this.fetchBrandPortalSearchResultsAsync(accessToken);
    }
    /**
     * Fetch search results for Play Portal (video-focused)
     * @param {string} accessToken - The access token for API authentication
     * @returns {Promise<{ pages: any[], files: any[], totalHits: number }>} Combined search results
     */
    async fetchPlayPortalSearchResultsAsync(accessToken) {
        const result = {
            pages: [],
            files: [],
            totalHits: 0
        };
        if (this.searchIncludePages) {
            const pages = await this.fetchPagesSearchResultAsync(accessToken);
            result.pages = pages.items;
            result.totalHits = pages.totalHits;
        }
        if (this.searchIncludeFiles) {
            const files = await this.fetchVideosSearchResultAsync(accessToken);
            result.files = files;
            result.totalHits += files.length;
        }
        return result;
    }
    /**
     * Fetch search results for Brand Portal (page-focused)
     * @param {string} accessToken - The access token for API authentication
     * @returns {Promise<{ pages: any[], files: any[], totalHits: number }>} Combined search results
     */
    async fetchBrandPortalSearchResultsAsync(accessToken) {
        const result = {
            pages: [],
            files: [],
            totalHits: 0
        };
        if (this.searchIncludePages) {
            const pages = await this.fetchPagesSearchResultAsync(accessToken);
            result.pages = pages.items;
            result.totalHits = pages.totalHits;
        }
        return result;
    }
    /**
     * Fetch video search results from the API
     * @param {string} accessToken - The access token for API authentication
     * @returns {Promise<any[]>} Array of video search results
     */
    async fetchVideosSearchResultAsync(accessToken) {
        const post = {
            searchTerm: this.query,
            fileTypes: ['video']
        };
        const response = await fetch(`${process.env.API_URL}/search/file?fields=any&access_token=${accessToken}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(post)
        });
        return await response.json();
    }
    /**
     * Fetch page search results from the API
     * @param {string} accessToken - The access token for API authentication
     * @returns {Promise<{ items: any[], totalHits: number }>} Page search results with total count
     */
    async fetchPagesSearchResultAsync(accessToken) {
        let response;
        try {
            response = await fetch(`/api/pages/search?q=${this.query}&culture=${this.culture}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                }
            });
        }
        catch (error) {
            console.error(`fetchPagesSearchResultAsync network failed`);
            return {
                items: [],
                totalHits: 0
            };
        }
        if (response.status !== 200) {
            console.error(`fetchPagesSearchResultAsync response.status ${response.status}`);
            return {
                items: [],
                totalHits: 0
            };
        }
        return await response.json();
    }
    /**
     * Initialize component and perform initial search
     * @returns {Promise<void>} only side effects
     */
    async firstUpdated() {
        await this.search();
    }
    /**
     * Render component based on site type
     * @returns {TemplateResult} Template for search results
     */
    render() {
        if (this.type == 'video')
            return this.renderPlayPortalSearch();
        return this.renderBrandPortalSearch();
    }
    /**
     * Render Play Portal search results (video-focused)
     * @returns {TemplateResult} Template for video search results
     */
    renderPlayPortalSearch() {
        const classes = {
            [`video-item-layout-${this.videoItemLayout}`]: true
        };
        return html ` <section id="search" class="search ${classMap(classes)}"> <div class="container"> <h1>${dictionary(this.culture, 'search', 'searchResult', [this.query])}</h1> <div class="files-result">${this.renderVideoSearchResults()}</div> ${this.renderPagesResult()} </div> </section> `;
    }
    /**
     * Render Brand Portal search results (page-focused)
     * @returns {TemplateResult} Template for page search results
     */
    renderBrandPortalSearch() {
        return html ` <section id="search" class="search"> <div class="container pages-container"> <h1>${dictionary(this.culture, 'search', 'searchResult', [this.query])}</h1> ${!this.searchIncludeFiles ? html ``
            : html ` <div class="files-result"> <h2>${dictionary(this.culture, 'search', 'files')}</h2> <p-files culture=${this.culture} enable-search search-query=${this.query} sort="name" sort-order="asc" layout="grid" padding="none" hide-header show-size show-dimensions show-type loading-items="3" ?ai-keywords=${this.searchIncludeAIKeywords} ></p-files> </div> `}
            ${this.renderPagesResult()}
          <div>
        </section>`;
    }
    /**
     * Render video search results section
     * @returns {TemplateResult | undefined} Template for video results or undefined if not enabled
     */
    renderVideoSearchResults() {
        if (!this.searchIncludeFiles)
            return;
        if (this.loading) {
            return html ` <ul class="results" aria-label=${dictionary(this.culture, 'search', 'loading')}> ${[...Array(4)].map(() => html ` <li>${getVideoSkeletonLayout(this.videoItemLayout)}</li> `)}
        </ul>
      `;
        }
        return html ` <h2>${dictionary(this.culture, 'search', 'videos')}</h2> <ul class="results" aria-label=${dictionary(this.culture, 'search', 'results')}> ${this.result.files?.map((result) => html `
            <li>
              <p-video-item
                layout=${this.videoItemLayout}
                heading-tag="h2"
                culture=${this.culture}
                class="result"
                poster=${result.poster}
                heading=${result.name}
                duration=${this.showRuntime ? result.length : ''}
                date=${this.showDate ? result.uploaded : ''}
                link="${this.videoBaseUrl}/${result.id}"
              >
              </p-video-item>
            </li>
          `)}
      </ul>
      ${this.result.files.length === 0
            ? html `<p class="empty">${dictionary(this.culture, 'search', 'noResults')}</p>`
            : ''}
    `;
    }
    /**
     * Render page search results section
     * @returns {TemplateResult | undefined} Template for page results or undefined if not enabled
     */
    renderPagesResult() {
        if (!this.searchIncludePages)
            return;
        if (this.loading) {
            return html ` <h2>${dictionary(this.culture, 'search', 'pages')}</h2> <ul class="results list" aria-label=${dictionary(this.culture, 'search', 'loading')}> <li> <p-skeleton height="30px" width="40%" margin="20px"></p-skeleton> <p-skeleton height="20px" width="70%"></p-skeleton> <p-skeleton height="20px" width="50%" margin="0"></p-skeleton> </li> <li> <p-skeleton height="30px" width="40%" margin="20px"></p-skeleton> <p-skeleton height="20px" width="70%"></p-skeleton> <p-skeleton height="20px" width="50%" margin="0"></p-skeleton> </li> <li> <p-skeleton height="30px" width="40%" margin="20px"></p-skeleton> <p-skeleton height="20px" width="70%"></p-skeleton> <p-skeleton height="20px" width="50%" margin="0"></p-skeleton> </li> <li> <p-skeleton height="30px" width="40%" margin="20px"></p-skeleton> <p-skeleton height="20px" width="70%"></p-skeleton> <p-skeleton height="20px" width="50%" margin="0"></p-skeleton> </li> </ul> `;
        }
        return html ` <h2>${dictionary(this.culture, 'search', 'pages')}</h2> <ul class="results list" aria-label=${dictionary(this.culture, 'search', 'results')}> ${this.result.pages?.map((result) => {
            return html `
            <li>
              <div>
                <h3><a href=${result.url}>${result.title || result.name}</a></h3>
                ${result.description ? html `<p>${result.description}</p>` : ''}
              </div>
            </li>
          `;
        })}
      </ul>
      ${this.result.pages.length === 0
            ? html `<p class="empty">${dictionary(this.culture, 'search', 'noResults')}</p>`
            : ''}
    `;
    }
};
__decorate([
    property({ type: String, reflect: true })
], PSearch.prototype, "query", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PSearch.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PSearch.prototype, "dark", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'video-base-url' })
], PSearch.prototype, "videoBaseUrl", void 0);
__decorate([
    property({ type: String })
], PSearch.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-runtime' })
], PSearch.prototype, "showRuntime", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-date' })
], PSearch.prototype, "showDate", void 0);
__decorate([
    property({ type: String })
], PSearch.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'video-item-layout' })
], PSearch.prototype, "videoItemLayout", void 0);
__decorate([
    property()
], PSearch.prototype, "result", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'search-include-pages' })
], PSearch.prototype, "searchIncludePages", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'search-include-files' })
], PSearch.prototype, "searchIncludeFiles", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'search-include-ai-keywords' })
], PSearch.prototype, "searchIncludeAIKeywords", void 0);
__decorate([
    query('#search')
], PSearch.prototype, "searchEl", void 0);
PSearch = __decorate([
    customElement('p-search')
    /**
     * PSearch component
     *
     * @class PSearch
     * @typedef {PImage}
     * @extends {LitElement}
     */
], PSearch);
export { PSearch };
